/**
 * DisplayedSubscriptionState derivation (frontend mirror) — METALARK-694
 *
 * Mirrors api/constants/DisplayedSubscriptionState.ts so the frontend can compute
 * a customer's displayed state from raw subscription fields without a separate API call.
 * If the backend already provides `displayedState` on the row, prefer that value.
 *
 * 15 canonical states, 16-row derivation table — see plans/payment-scenarios-and-states.md
 */

export const DisplayedSubscriptionState = {
    TRIAL_ACTIVE: 'TRIAL_ACTIVE',
    TRIAL_ENDING_SOON: 'TRIAL_ENDING_SOON',
    TRIAL_EXPIRED: 'TRIAL_EXPIRED',
    ACTIVE: 'ACTIVE',
    ACTIVE_SCHEDULED_CANCEL: 'ACTIVE_SCHEDULED_CANCEL',
    ACTIVE_PLAN_CHANGE_PENDING: 'ACTIVE_PLAN_CHANGE_PENDING',
    PAYMENT_PENDING: 'PAYMENT_PENDING',
    PAST_DUE: 'PAST_DUE',
    UNPAID: 'UNPAID',
    INCOMPLETE: 'INCOMPLETE',
    CANCELED_IN_GRACE: 'CANCELED_IN_GRACE',
    EXPIRED: 'EXPIRED',
    PENDING_DELETION: 'PENDING_DELETION',
    NO_SUBSCRIPTION: 'NO_SUBSCRIPTION',
    PAUSED: 'PAUSED',
};

// METALARK-789: deriveDisplayedState only references three SUBSCRIPTION_LIFECYCLE
// thresholds — caching just those keeps admin-internal values (adminWarningDays,
// hardDeleteGraceDays, incompleteAbandonDays) out of the customer-admin
// bundle's runtime state. Components that need the full set fetch the
// role-scoped /api/v1/subscription-config response directly (the backend
// returns admin-only fields only when the caller is SYSTEM_ADMIN).
//
// Defaults match SUBSCRIPTION_LIFECYCLE defaults in
// api/config/notificationSchedule.ts; setLifecycleConfig() overwrites these
// once a fetch resolves so backend env overrides reach the frontend.
let _lifecycleConfig = {
    trialEndingSoonDays:      3,
    paymentPendingWindowMins: 5,
    deletionGraceDays:        30,
};

/**
 * Overwrite the cached derive-required lifecycle constants. Accepts the
 * `lifecycle` object from a /api/v1/subscription-config response — only the
 * three keys deriveDisplayedState actually reads are picked off; everything
 * else is ignored so admin-internal fields don't accidentally land in the
 * shared cache that ships in the customer-admin bundle.
 */
export function setLifecycleConfig(partial) {
    if (!partial || typeof partial !== 'object') return;
    const next = { ..._lifecycleConfig };
    if (typeof partial.trialEndingSoonDays === 'number')      next.trialEndingSoonDays      = partial.trialEndingSoonDays;
    if (typeof partial.paymentPendingWindowMins === 'number') next.paymentPendingWindowMins = partial.paymentPendingWindowMins;
    if (typeof partial.deletionGraceDays === 'number')        next.deletionGraceDays        = partial.deletionGraceDays;
    _lifecycleConfig = next;
}

/** Read the current cached lifecycle config (defaults until fetched). */
export function getLifecycleConfig() {
    return _lifecycleConfig;
}

function toDate(v) {
    if (!v) return null;
    if (v instanceof Date) return v;
    return new Date(v);
}

/**
 * METALARK-789: trust the canonical `state` column on the subscription row
 * when it's populated. The state column is set transactionally by every
 * webhook / sweep / admin transition through SubscriptionStateMachine
 * .transitionState(), so it's the authoritative source of truth — derive
 * is only needed for legacy rows that pre-date the column.
 *
 * The only displayed-only modifier we layer on top is TRIAL_ENDING_SOON,
 * which is computed from trialEnd + the trialEndingSoonDays threshold.
 *
 * @param {object|null} subscription - Subscription fields
 * @param {Date} [now] - Current time (injectable for testing)
 * @param {boolean} [hasPaymentMethod] - Whether a payment method is on file
 * @returns {string} DisplayedSubscriptionState value
 */
/**
 * Pick the most-relevant subscription row from a list returned by
 * subscriptionService.getByCustomerId (which often contains stale rows —
 * trial, canceled, pending_deletion — alongside the live one). The
 * server returns rows in createdDate desc by default; that picks the
 * NEWEST row regardless of state, so a customer who cancels and
 * resubscribes ends up with the new ACTIVE row first (good), but a
 * customer mid-resubscribe-flow whose new ACTIVE row isn't created yet
 * could see the stale CANCELED_IN_GRACE row first (bad).
 *
 * Sort order: active states first (state-machine "the customer is using
 * this right now" set), then by recency (updatedDate, fallback
 * createdDate). Returns the first row, or null when the list is empty.
 *
 * Shared across customer-360.tag (header), customer-billing-tab.tag,
 * customer-overview-tab.tag, and any other surface that needs to render
 * "the" subscription. Keep this helper in sync with the same logic
 * inlined elsewhere — drift is exactly the bug it exists to prevent.
 */
export function pickActiveSubscription(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const ACTIVE_STATES = new Set([
        'ACTIVE', 'ACTIVE_SCHEDULED_CANCEL', 'TRIAL_ACTIVE',
        'PAYMENT_PENDING', 'INCOMPLETE',
    ]);
    const sorted = rows.slice().sort((a, b) => {
        const aActive = ACTIVE_STATES.has(a?.state) ? 1 : 0;
        const bActive = ACTIVE_STATES.has(b?.state) ? 1 : 0;
        if (aActive !== bActive) return bActive - aActive;
        const ta = new Date(a?.updatedDate || a?.createdDate || 0).getTime();
        const tb = new Date(b?.updatedDate || b?.createdDate || 0).getTime();
        return tb - ta;
    });
    return sorted[0] || null;
}

export function displayedFromState(subscription, now = new Date(), hasPaymentMethod = false) {
    if (!subscription) return DisplayedSubscriptionState.NO_SUBSCRIPTION;
    const stored = subscription.state;
    if (typeof stored !== 'string' || !stored) {
        // Legacy row without the state column populated — fall back to
        // deriving from raw fields. Once the row is touched by any
        // METALARK-789 transition (webhook / sweep / admin) it gets a
        // stored state and this branch goes unused for that customer.
        return deriveDisplayedState(subscription, now, hasPaymentMethod);
    }
    // TRIAL_ACTIVE → TRIAL_ENDING_SOON if the trial wraps up within the
    // configured window AND the customer has no payment method on file.
    // Other displayed-only modifiers (UNPAID, PAUSED) collapse to
    // PAST_DUE / CANCELED_IN_GRACE in storage and don't need re-expansion
    // for the customer-facing display.
    if (stored === 'TRIAL_ACTIVE') {
        const trialEnd = toDate(subscription.trialEnd);
        if (trialEnd && trialEnd > now) {
            const daysUntilEnd = (trialEnd.getTime() - now.getTime()) / 86400000;
            if (daysUntilEnd <= _lifecycleConfig.trialEndingSoonDays && !hasPaymentMethod) {
                return DisplayedSubscriptionState.TRIAL_ENDING_SOON;
            }
        }
    }
    return stored;
}

/**
 * Derive the canonical displayed state from a subscription row.
 * Legacy path used when the new `state` column isn't populated;
 * displayedFromState() prefers the column when it is.
 *
 * @param {object|null} subscription - Subscription fields (from customer.subscription or direct row)
 * @param {Date} [now] - Current time (injectable for testing)
 * @param {boolean} [hasPaymentMethod] - Whether a payment method is on file
 * @returns {string} DisplayedSubscriptionState value
 */
export function deriveDisplayedState(subscription, now = new Date(), hasPaymentMethod = false) {
    if (!subscription) return DisplayedSubscriptionState.NO_SUBSCRIPTION;

    const status = (subscription.status === 'trialing') ? 'trial' : subscription.status;
    const trialEnd = toDate(subscription.trialEnd);
    const billingPeriodStart = toDate(subscription.billingPeriodStart);
    const billingPeriodEnd = toDate(subscription.billingPeriodEnd);
    const cancelAtPeriodEnd = !!subscription.cancelAtPeriodEnd;
    const canceledAt = toDate(subscription.canceledAt);
    const gracePeriodEnd = toDate(subscription.gracePeriodEnd);
    const createdDate = toDate(subscription.createdDate) || new Date();

    // Row 16: Paused
    if (status === 'paused') return DisplayedSubscriptionState.PAUSED;

    // Row 12: Incomplete
    if (status === 'incomplete' || status === 'incomplete_expired') {
        return DisplayedSubscriptionState.INCOMPLETE;
    }

    // METALARK-789: pull lifecycle thresholds from the runtime cache, so
    // backend env overrides (TRIAL_ENDING_SOON_DAYS, DELETION_GRACE_DAYS,
    // PAYMENT_PENDING_WINDOW_MINUTES) reach the frontend after the config
    // fetch resolves. Defaults match SUBSCRIPTION_LIFECYCLE before fetch.
    const cfg = _lifecycleConfig;
    const paymentPendingWindowMs = cfg.paymentPendingWindowMins * 60 * 1000;

    // Rows 1–4: Trial states
    if (status === 'trial' || status === 'trialing') {
        if (trialEnd && trialEnd > now) {
            const daysUntilEnd = (trialEnd.getTime() - now.getTime()) / 86400000;
            if (daysUntilEnd <= cfg.trialEndingSoonDays && !hasPaymentMethod) {
                return DisplayedSubscriptionState.TRIAL_ENDING_SOON;
            }
            return DisplayedSubscriptionState.TRIAL_ACTIVE;
        }
        if (gracePeriodEnd && gracePeriodEnd > now) {
            return DisplayedSubscriptionState.TRIAL_EXPIRED;
        }
        return DisplayedSubscriptionState.EXPIRED;
    }

    // Rows 13–15: Canceled states
    if (status === 'canceled') {
        if (canceledAt && gracePeriodEnd) {
            if (gracePeriodEnd > now) return DisplayedSubscriptionState.CANCELED_IN_GRACE;
            const daysPastGrace = (now.getTime() - gracePeriodEnd.getTime()) / 86400000;
            return daysPastGrace > cfg.deletionGraceDays
                ? DisplayedSubscriptionState.PENDING_DELETION
                : DisplayedSubscriptionState.EXPIRED;
        }
        return DisplayedSubscriptionState.EXPIRED;
    }

    // Rows 10–11
    if (status === 'past_due') return DisplayedSubscriptionState.PAST_DUE;
    if (status === 'unpaid') return DisplayedSubscriptionState.UNPAID;

    // Rows 5–9: Active states
    if (status === 'active') {
        const hasBillingDates = billingPeriodStart !== null && billingPeriodEnd !== null;
        if (!hasBillingDates) {
            const msSinceCreation = now.getTime() - createdDate.getTime();
            return msSinceCreation < paymentPendingWindowMs
                ? DisplayedSubscriptionState.PAYMENT_PENDING
                : DisplayedSubscriptionState.INCOMPLETE;
        }
        if (billingPeriodEnd && billingPeriodEnd < now) {
            return DisplayedSubscriptionState.PAST_DUE;
        }
        return cancelAtPeriodEnd
            ? DisplayedSubscriptionState.ACTIVE_SCHEDULED_CANCEL
            : DisplayedSubscriptionState.ACTIVE;
    }

    return DisplayedSubscriptionState.EXPIRED;
}

/**
 * Get the DaisyUI badge class for a displayed state.
 */
export function badgeClassForState(state) {
    const map = {
        TRIAL_ACTIVE: 'badge-info',
        TRIAL_ENDING_SOON: 'badge-warning',
        TRIAL_EXPIRED: 'badge-warning',
        ACTIVE: 'badge-success',
        ACTIVE_SCHEDULED_CANCEL: 'badge-warning',
        ACTIVE_PLAN_CHANGE_PENDING: 'badge-info',
        PAYMENT_PENDING: 'badge-info',
        PAST_DUE: 'badge-error',
        UNPAID: 'badge-error',
        INCOMPLETE: 'badge-ghost',
        CANCELED_IN_GRACE: 'badge-error',
        EXPIRED: 'badge-ghost',
        PENDING_DELETION: 'badge-error',
        NO_SUBSCRIPTION: 'badge-ghost',
        PAUSED: 'badge-warning',
    };
    return map[state] || 'badge-ghost';
}

/**
 * Get the localized label for a displayed state.
 * Caller must pass in a locale service.
 */
export function labelForState(state, locale) {
    if (!state) return '';
    const key = `displayed_state_${state.toLowerCase()}`;
    return (locale && locale.t && locale.t(key)) || state;
}

/**
 * Compute a human-readable "signal" — the most urgent thing about a customer.
 * Returns a short sentence describing the urgency, or "Healthy" for non-attention states.
 */
export function signalForCustomer(customer, locale) {
    const sub = customer?.subscription;
    const state = customer?.displayedState || deriveDisplayedState(sub);
    const now = new Date();
    const t = (k, params) => (locale && locale.t && locale.t(k, params)) || k;

    if (state === 'TRIAL_ENDING_SOON' && sub?.trialEnd) {
        const days = Math.max(0, Math.ceil((new Date(sub.trialEnd).getTime() - now.getTime()) / 86400000));
        if (days <= 0) return t('customer_list_signal_trial_ends_today');
        if (days === 1) return t('customer_list_signal_trial_ends_tomorrow');
        return t('customer_list_signal_trial_ends_days', { count: days });
    }

    if (state === 'TRIAL_ACTIVE' && sub?.trialEnd) {
        const days = Math.max(0, Math.ceil((new Date(sub.trialEnd).getTime() - now.getTime()) / 86400000));
        return t('customer_list_signal_trial_days_left', { count: days });
    }

    if (state === 'PAST_DUE' && sub?.billingPeriodEnd) {
        const days = Math.max(0, Math.ceil((now.getTime() - new Date(sub.billingPeriodEnd).getTime()) / 86400000));
        return t('customer_list_signal_days_overdue', { count: days });
    }

    if (state === 'ACTIVE_SCHEDULED_CANCEL' && sub?.billingPeriodEnd) {
        return t('customer_list_signal_cancels_on', { date: new Date(sub.billingPeriodEnd).toLocaleDateString() });
    }

    if (state === 'CANCELED_IN_GRACE' && sub?.gracePeriodEnd) {
        const days = Math.max(0, Math.ceil((new Date(sub.gracePeriodEnd).getTime() - now.getTime()) / 86400000));
        return t('customer_list_signal_grace_days', { count: days });
    }

    if (state === 'EXPIRED' && sub?.gracePeriodEnd) {
        const daysPast = Math.ceil((now.getTime() - new Date(sub.gracePeriodEnd).getTime()) / 86400000);
        const daysUntilDeletion = _lifecycleConfig.deletionGraceDays - daysPast;
        // Past the threshold — the sweep just hasn't moved the row to
        // PENDING_DELETION yet. Show the same label the row will have in
        // a few minutes rather than "Deletion in 0 days".
        if (daysUntilDeletion <= 0) return t('customer_list_signal_pending_deletion');
        return t('customer_list_signal_deletion_in_days', { count: daysUntilDeletion });
    }

    if (state === 'PENDING_DELETION') return t('customer_list_signal_pending_deletion');
    if (state === 'UNPAID') return t('customer_list_signal_unpaid');
    if (state === 'PAYMENT_PENDING') return t('customer_list_signal_payment_pending');
    if (state === 'INCOMPLETE') return t('customer_list_signal_incomplete');
    if (state === 'NO_SUBSCRIPTION') return t('customer_list_signal_no_subscription');
    if (state === 'PAUSED') return t('customer_list_signal_paused');
    if (state === 'ACTIVE') return t('customer_list_signal_healthy');
    return '';
}

/**
 * Urgency weight: higher = more urgent. Used for sorting.
 * Customers with urgency > 0 float to the top of the list.
 */
export function urgencyForState(state) {
    const map = {
        PAST_DUE: 100,
        UNPAID: 95,
        TRIAL_ENDING_SOON: 80,
        CANCELED_IN_GRACE: 75,
        PENDING_DELETION: 70,
        EXPIRED: 60,
        PAYMENT_PENDING: 50,
        INCOMPLETE: 40,
        ACTIVE_SCHEDULED_CANCEL: 30,
        TRIAL_EXPIRED: 25,
        PAUSED: 20,
        TRIAL_ACTIVE: 0,
        ACTIVE: 0,
        NO_SUBSCRIPTION: 0,
    };
    return map[state] ?? 0;
}

/**
 * Compute monthly recurring revenue contribution in dollars for a customer.
 * Monthly plans contribute full amount; annual plans are divided by 12.
 */
export function computeMrr(customer) {
    const sub = customer?.subscription;
    if (!sub) return 0;
    const state = customer?.displayedState || deriveDisplayedState(sub);
    const paying = ['ACTIVE', 'ACTIVE_SCHEDULED_CANCEL', 'PAST_DUE'];
    if (!paying.includes(state)) return 0;
    const interval = sub.billingInterval;
    const isAnnual = interval === 'annual' || interval === 'year';

    // Prefer explicit price fields from the server (priceCents or amount/currency).
    // Fall back to $250/mo, $2500/yr when pricing isn't on the row yet.
    let monthlyDollars;
    if (typeof sub.priceCents === 'number') {
        monthlyDollars = sub.priceCents / 100;
        if (isAnnual) monthlyDollars = monthlyDollars / 12;
    } else if (typeof sub.amount === 'number') {
        monthlyDollars = isAnnual ? sub.amount / 12 : sub.amount;
    } else {
        monthlyDollars = isAnnual ? 2500 / 12 : 250;
    }
    return Math.round(monthlyDollars);
}
